import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./PlainSightPaywall.module.scss"

/**
 * PlainSightPaywall (`/plainsight`) — an original, self-contained
 * FEATURE-SPECIFIC paywall for Private Photo Vault Premium. Paywall type rolled
 * at random for this run: FEATURE-SPECIFIC. The gated feature is the app's real
 * APP DISGUISE (with Decoy Vault + only-you lock as supporting concealment).
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot message this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The entire concept is built to that brief: the pitch is
 * SECURITY & PRIVACY, specifically privacy-through-CONCEALMENT.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * A taller wall is not the only way to be safe. The safest vault is the one no
 * one thinks to look for. Private Photo Vault Premium can DISGUISE the app on
 * your home screen so it reads as an ordinary Calculator / Clock / Notes tile —
 * hidden in plain sight. The hero is a working home-screen SPRINGBOARD: the
 * vault sits among everyday apps, conspicuous and brand-blue ("anyone can spot
 * it"), and when you pick a disguise it MORPHS — icon and label crossfade — and
 * goes quiet like every other tile ("now no one can"). A reveal flips the phone
 * to show that behind the disguise it opens to YOUR private photos, and that
 * only you know the way in.
 *
 * ── Divergence (see claude-paywalls/_ledger.md — 90+ prior concepts) ────────
 * Layout      : a HOME-SCREEN SPRINGBOARD inside a phone (an app grid) — no prior
 *               concept is a device home screen. Not a hero+bullets card, not the
 *               biometric console (Imprint), CCTV wall (Caught), frosted glass
 *               (Glyph), vault door, or receipt.
 * Interaction : DISGUISE PICKER — choose what the vault pretends to be and watch
 *               the tile morph & camouflage into the grid; a reveal flips the
 *               phone to the open vault. Camouflage, not capture/hold/tap-beat.
 * Metaphor    : CAMOUFLAGE / hidden-in-plain-sight — concealment, not a stronger
 *               lock. A brand-new value frame in the set.
 * Register    : clever, quietly confident, a touch of secret-keeping delight.
 * Art dir     : LIGHT cool porcelain "device desk" (deliberately NOT Imprint's
 *               obsidian/teal) — brand blue is the EXPOSED vault, neutral slate
 *               is the HIDDEN one; the contrast IS the story.
 * Value frame : privacy via CONCEALMENT + plausible deniability (Decoy).
 * Hero motif  : a vault app that disguises itself into a home screen of apps.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * App Disguise, Decoy Vault and the biometric / PIN "only-you" lock are all REAL
 * Private Photo Vault features. Copy never claims the on-screen demo IS the
 * security — it names the real mechanisms. No fake scarcity, no countdown, no
 * data-loss-on-expiration threat, no false guarantees. "Save 79%" is real
 * arithmetic from live product data. Yearly is the featured, default, most
 * prominent price; monthly is small & secondary. The 7-day free trial is the
 * real trial in the product data. Interior tiles are labelled Unsplash demo
 * photos, never the user's library.
 */

// ── the everyday apps the vault hides among (muted decoys) ──────────────────
type Glyph =
  | "weather"
  | "clock"
  | "notes"
  | "music"
  | "maps"
  | "camera"
  | "mail"
  | "calendar"
  | "files"
  | "settings"
  | "calc"

const DECOYS: { key: string; label: string; glyph: Glyph }[] = [
  { key: "weather", label: "Weather", glyph: "weather" },
  { key: "maps", label: "Maps", glyph: "maps" },
  { key: "music", label: "Music", glyph: "music" },
  { key: "mail", label: "Mail", glyph: "mail" },
  { key: "camera", label: "Camera", glyph: "camera" },
  { key: "calendar", label: "Calendar", glyph: "calendar" },
  { key: "clockd", label: "Clock", glyph: "clock" },
  { key: "files", label: "Files", glyph: "files" },
  { key: "settings", label: "Settings", glyph: "settings" },
  { key: "notesd", label: "Notes", glyph: "notes" },
  { key: "musicd", label: "Podcasts", glyph: "music" },
]

// ── the disguises you can wear (index 0 = the true, exposed identity) ────────
const DISGUISES: { key: string; label: string; glyph: Glyph | "vault" }[] = [
  { key: "vault", label: "Vault", glyph: "vault" },
  { key: "calc", label: "Calculator", glyph: "calc" },
  { key: "clock", label: "Clock", glyph: "clock" },
  { key: "notes", label: "Notes", glyph: "notes" },
]

// private photos behind the disguise — bundled Unsplash demo tiles (no egress)
const VAULT_PHOTOS = [
  "/recall/sunset-1.jpg",
  "/recall/beach-2.jpg",
  "/recall/dog-1.jpg",
  "/recall/coffee-2.jpg",
]

// the three real layers of concealment, framed as "hidden three ways"
const LAYERS = [
  {
    n: "01",
    name: "Disguised on your home screen",
    line: "The app wears an ordinary icon — a calculator, a clock. Nothing to find.",
    glyph: "disguise" as const,
  },
  {
    n: "02",
    name: "A decoy for prying eyes",
    line: "A second code opens a harmless album, so a glance over your shoulder sees nothing real.",
    glyph: "decoy" as const,
  },
  {
    n: "03",
    name: "Only you hold the key",
    line: "Face ID, Touch ID or a PIN you choose is the one and only way in.",
    glyph: "lock" as const,
  },
]

// ── tiny app glyphs (drawn, so nothing is fetched) ──────────────────────────
const AppGlyph = ({ glyph }: { glyph: Glyph | "vault" }) => {
  switch (glyph) {
    case "vault":
      return (
        <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
          <rect
            x="4"
            y="10"
            width="16"
            height="11"
            rx="2.5"
            fill="currentColor"
          />
          <path
            d="M7.5 10V7.5a4.5 4.5 0 0 1 9 0V10"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.1"
          />
          <circle cx="12" cy="15.4" r="1.7" fill="#fff" />
        </svg>
      )
    case "calc":
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
          <rect
            x="5"
            y="3.5"
            width="14"
            height="17"
            rx="2.4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <rect
            x="7.6"
            y="6"
            width="8.8"
            height="3"
            rx="1"
            fill="currentColor"
          />
          <g fill="currentColor">
            <circle cx="8.4" cy="12.4" r="1" />
            <circle cx="12" cy="12.4" r="1" />
            <circle cx="15.6" cy="12.4" r="1" />
            <circle cx="8.4" cy="16" r="1" />
            <circle cx="12" cy="16" r="1" />
            <circle cx="15.6" cy="16" r="1" />
          </g>
        </svg>
      )
    case "clock":
      return (
        <svg viewBox="0 0 24 24" width="25" height="25" aria-hidden="true">
          <circle
            cx="12"
            cy="12"
            r="8.2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
          />
          <path
            d="M12 7.4V12l3.2 2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
          />
        </svg>
      )
    case "notes":
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
          <rect
            x="5.5"
            y="3.5"
            width="13"
            height="17"
            rx="2.3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M8.5 8h7M8.5 11.5h7M8.5 15h4.5"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        </svg>
      )
    case "weather":
      return (
        <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
          <circle cx="9" cy="9.5" r="3.4" fill="currentColor" opacity="0.9" />
          <path
            d="M8 18a3.6 3.6 0 0 1 .3-7.1 4.6 4.6 0 0 1 8.7 1.3A3.3 3.3 0 0 1 16.5 18H8z"
            fill="currentColor"
          />
        </svg>
      )
    case "music":
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
          <path
            d="M15.5 5.5 9 7v8.6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
          />
          <circle cx="7.2" cy="15.8" r="2.3" fill="currentColor" />
          <circle cx="13.7" cy="14.2" r="2.3" fill="currentColor" />
          <path
            d="M15.5 5.5v8.7"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
          />
        </svg>
      )
    case "maps":
      return (
        <svg viewBox="0 0 24 24" width="25" height="25" aria-hidden="true">
          <path
            d="M12 3.6c-3.2 0-5.5 2.4-5.5 5.4 0 3.9 5.5 11 5.5 11s5.5-7.1 5.5-11c0-3-2.3-5.4-5.5-5.4z"
            fill="currentColor"
          />
          <circle cx="12" cy="9" r="2" fill="#fff" />
        </svg>
      )
    case "camera":
      return (
        <svg viewBox="0 0 24 24" width="25" height="25" aria-hidden="true">
          <rect
            x="3.5"
            y="7"
            width="17"
            height="12"
            rx="2.6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <circle
            cx="12"
            cy="13"
            r="3.2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M8.5 7l1.2-2h4.6l1.2 2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "mail":
      return (
        <svg viewBox="0 0 24 24" width="25" height="25" aria-hidden="true">
          <rect
            x="3.5"
            y="5.5"
            width="17"
            height="13"
            rx="2.4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M4.5 7.5 12 13l7.5-5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      )
    case "calendar":
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
          <rect
            x="4"
            y="5"
            width="16"
            height="15"
            rx="2.4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M4 9.5h16M8 3.5v3M16 3.5v3"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <rect
            x="7"
            y="12"
            width="3"
            height="3"
            rx="0.7"
            fill="currentColor"
          />
        </svg>
      )
    case "files":
      return (
        <svg viewBox="0 0 24 24" width="25" height="25" aria-hidden="true">
          <path
            d="M4 7.5A2 2 0 0 1 6 5.5h3.6l1.8 2h6.6a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"
            fill="currentColor"
          />
        </svg>
      )
    case "settings":
      return (
        <svg viewBox="0 0 24 24" width="25" height="25" aria-hidden="true">
          <circle
            cx="12"
            cy="12"
            r="3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
          />
          <path
            d="M12 3.5v2.3M12 18.2v2.3M20.5 12h-2.3M5.8 12H3.5M18 6l-1.6 1.6M7.6 16.4 6 18M18 18l-1.6-1.6M7.6 7.6 6 6"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      )
  }
}

const LayerGlyph = ({ glyph }: { glyph: "disguise" | "decoy" | "lock" }) => {
  if (glyph === "disguise")
    return (
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path
          d="M3 8.5c3-1.6 5.4-1.6 8.4 0M12.6 8.5c3-1.6 5.4-1.6 8.4 0"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        />
        <circle
          cx="7.2"
          cy="12.6"
          r="3.1"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
        />
        <circle
          cx="16.8"
          cy="12.6"
          r="3.1"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
        />
        <path
          d="M10.3 12.6c.6-.7 2.8-.7 3.4 0"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        />
      </svg>
    )
  if (glyph === "decoy")
    return (
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <rect
          x="3.5"
          y="5"
          width="12"
          height="12"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
        />
        <rect
          x="8.5"
          y="9"
          width="12"
          height="12"
          rx="2"
          fill="currentColor"
          opacity="0.28"
          stroke="currentColor"
          strokeWidth="1.9"
        />
      </svg>
    )
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <rect x="5" y="10.5" width="14" height="9.5" rx="2" fill="currentColor" />
      <path
        d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
      />
    </svg>
  )
}

type PlanKey = "yearly" | "monthly"

const PlainSightPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // disguise = index into DISGUISES (0 = exposed "Vault"); revealed = phone flipped
  const [disguise, setDisguise] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const hidden = disguise > 0
  const current = DISGUISES[disguise]

  const timers = useRef<number[]>([])
  const userActed = useRef(false)
  const clearTimers = useCallback(() => {
    timers.current.forEach((t) => clearTimeout(t))
    timers.current = []
  }, [])

  // auto-teach on mount: the vault starts EXPOSED (blue, obvious), then slips on
  // a disguise and goes quiet — so the resting state we sell is "hidden". A real
  // tap cancels the demo.
  useEffect(() => {
    if (reduceMotion) {
      setDisguise(1)
      return
    }
    timers.current.push(
      window.setTimeout(() => {
        if (!userActed.current) setDisguise(1)
      }, 1150),
    )
    return clearTimers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  const pickDisguise = useCallback((i: number) => {
    userActed.current = true
    setRevealed(false)
    setDisguise(i)
  }, [])

  const toggleReveal = useCallback(() => {
    userActed.current = true
    setRevealed((r) => !r)
  }, [])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  // fixed grid: 12 slots, vault at index 5
  const VAULT_SLOT = 5
  const grid: ({ vault: true } | (typeof DECOYS)[number])[] = []
  let d = 0
  for (let i = 0; i < 12; i++) {
    if (i === VAULT_SLOT) grid.push({ vault: true })
    else grid.push(DECOYS[d++ % DECOYS.length])
  }

  return (
    <div
      className={`${styles.root} ${hidden ? styles.isHidden : styles.isExposed}`}
    >
      <div className={styles.wash} aria-hidden="true" />

      {/* header — brand clarity in the first glance */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15">
              <rect
                x="4"
                y="10"
                width="16"
                height="11"
                rx="2.4"
                fill="currentColor"
              />
              <path
                d="M7.5 10V7.5a4.5 4.5 0 0 1 9 0V10"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </span>
          <span className={styles.brandName}>
            Private Photo Vault
            <span className={styles.brandTag}>Premium</span>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={() => native.postNativeEvent("close")}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15">
            <path
              d="M6 6l12 12M18 6L6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* intro */}
      <section className={styles.intro}>
        <span className={styles.kicker}>
          <span className={styles.kickerDot} aria-hidden="true" />
          App Disguise
        </span>
        <h1 className={styles.title}>
          The safest vault is the one
          <br />
          <span className={styles.titleAccent}>no one looks for.</span>
        </h1>
        <p className={styles.sub}>
          Premium hides the app itself. On your home screen it looks like an
          ordinary tile — while your photos stay locked inside, in plain sight.
        </p>
      </section>

      {/* ── the interactive home-screen springboard ─────────────────────────── */}
      <section className={styles.stage}>
        <div className={styles.statusRow}>
          <span
            className={`${styles.statusPill} ${hidden ? styles.pillHidden : styles.pillExposed}`}
          >
            <span className={styles.pillDot} aria-hidden="true" />
            {hidden
              ? `Disguised as ${current.label}`
              : "Exposed — anyone can spot it"}
          </span>
        </div>

        <div className={`${styles.phone} ${revealed ? styles.flipped : ""}`}>
          <div className={styles.phoneInner}>
            {/* FRONT: the home screen the vault hides in */}
            <div className={styles.faceFront}>
              <div className={styles.statusbar} aria-hidden="true">
                <span>9:41</span>
                <span className={styles.sbIcons}>
                  <i />
                  <i />
                  <b />
                </span>
              </div>
              <div className={styles.springboard}>
                {grid.map((item, i) =>
                  "vault" in item ? (
                    <div
                      key="vault"
                      className={`${styles.appTile} ${styles.vaultTile} ${hidden ? styles.tileHidden : styles.tileExposed}`}
                    >
                      <span className={styles.spotlight} aria-hidden="true" />
                      <span className={styles.icon} key={current.key}>
                        <AppGlyph glyph={current.glyph} />
                      </span>
                      <span className={styles.appLabel}>{current.label}</span>
                    </div>
                  ) : (
                    <div key={item.key} className={styles.appTile}>
                      <span className={styles.icon}>
                        <AppGlyph glyph={item.glyph} />
                      </span>
                      <span className={styles.appLabel}>{item.label}</span>
                    </div>
                  ),
                )}
              </div>
              <div className={styles.dock} aria-hidden="true">
                {(["camera", "mail", "music", "maps"] as Glyph[]).map((g) => (
                  <span key={g} className={styles.dockIcon}>
                    <AppGlyph glyph={g} />
                  </span>
                ))}
              </div>
            </div>

            {/* BACK: what only you can open */}
            <div className={styles.faceBack}>
              <div className={styles.vaultHead}>
                <span className={styles.vaultHeadIcon} aria-hidden="true">
                  <AppGlyph glyph="vault" />
                </span>
                <span>
                  <b>Private Photo Vault</b>
                  <small>Only you can open this</small>
                </span>
              </div>
              <div className={styles.vaultGrid}>
                {VAULT_PHOTOS.map((src, i) => (
                  <span
                    key={src}
                    className={styles.photo}
                    style={{ ["--i" as any]: i }}
                  >
                    <img src={src} alt="" draggable={false} />
                  </span>
                ))}
              </div>
              <p className={styles.vaultCredit}>Demo photos from Unsplash</p>
            </div>
          </div>
        </div>

        {/* controls: choose a disguise, or reveal what's inside */}
        <div className={styles.controls}>
          <span className={styles.controlsLabel}>Disguise it as</span>
          <div
            className={styles.picker}
            role="group"
            aria-label="Choose a disguise"
          >
            {DISGUISES.slice(1).map((opt, idx) => {
              const i = idx + 1
              const active = disguise === i && !revealed
              return (
                <button
                  key={opt.key}
                  className={`${styles.chip} ${active ? styles.chipActive : ""}`}
                  onClick={() => pickDisguise(i)}
                  aria-pressed={active}
                >
                  <span className={styles.chipIcon} aria-hidden="true">
                    <AppGlyph glyph={opt.glyph} />
                  </span>
                  {opt.label}
                </button>
              )
            })}
          </div>
          <button
            className={`${styles.reveal} ${revealed ? styles.revealOn : ""}`}
            onClick={toggleReveal}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              {revealed ? (
                <path
                  d="M3 12s3.5-6.5 9-6.5S21 12 21 12M3 4l18 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                />
              ) : (
                <>
                  <path
                    d="M3 12s3.5-6.5 9-6.5S21 12 21 12 17.5 18.5 12 18.5 3 12 3 12z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinejoin="round"
                  />
                  <circle cx="12" cy="12" r="2.6" fill="currentColor" />
                </>
              )}
            </svg>
            {revealed ? "Hide it again" : "Show what only you can open"}
          </button>
        </div>
      </section>

      {/* ── hidden three ways: the real concealment layers ──────────────────── */}
      <ul className={styles.layers}>
        {LAYERS.map((l, i) => (
          <li key={l.n} className={styles.layer} style={{ ["--i" as any]: i }}>
            <span className={styles.layerNum} aria-hidden="true">
              <LayerGlyph glyph={l.glyph} />
            </span>
            <span className={styles.layerText}>
              <span className={styles.layerName}>{l.name}</span>
              <span className={styles.layerLine}>{l.line}</span>
            </span>
          </li>
        ))}
      </ul>

      {/* honest, compliant reassurance */}
      <p className={styles.assure}>
        <span className={styles.assureMark} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="13" height="13">
            <path
              d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
            <path
              d="M9 12.2l2.2 2.2L15 10.4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        App Disguise, the Decoy Vault and the biometric lock are real Private
        Photo Vault features. Your photos always stay on your device unless you
        back them up — nothing here is ever posted or shared.
      </p>

      {/* ── PRICING: yearly featured, monthly small & secondary ─────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${plan === "yearly" ? styles.selected : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyHead}>
            <span className={styles.yearlyName}>Yearly · best value</span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>Save {savingsPct}%</span>
            )}
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyPerMo}>
            7 days free, then about {yearlyPerMonth}/mo billed yearly
          </span>
        </button>

        <button
          className={`${styles.monthly} ${plan === "monthly" ? styles.monthlySel : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <div className={styles.dockCta}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? "Start free — then hide my vault"
              : "Start monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `7 days free, then ${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default PlainSightPaywall
